// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file List.tsx
 * @input Uses React, ReactNode, StyleXStyles, theme tokens, ListContext
 * @output Exports List component, ListProps, ListDensity, ListStyle types
 * @position Core implementation; consumed by index.ts, tested by List.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/List/List.doc.mjs
 * - /packages/core/src/List/List.test.tsx
 * - /packages/core/src/List/index.ts
 * - /apps/storybook/stories/List.stories.tsx
 * - /packages/cli/templates/blocks/components/List/ (showcase blocks)
 */
import { useId, useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
import { ListContext } from "./ListContext.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  list: {
    kogj98: "x1ghz6dp",
    kZCmMZ: "x1c1uobl",
    kH6xsr: "x3ct3a4",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1lsbc85",
    $$css: true
  },
  withDividers: {
    kOIVth: "xxhr3t",
    $$css: true
  },
  withCounter: {
    kt6KFK: "xif0320",
    $$css: true
  }
};
const _temp = {
  kt6KFK: "x1khind5",
  "$$css": true
};
const dynamicStyles = {
  counterStart: value => [_temp, {
    "--x-counterReset": `astryx-list ${value}` != null ? `astryx-list ${value}` : undefined
  }]
};

// =============================================================================
// Component
// =============================================================================

/**
 * A vertical list component for rendering collections of items.
 *
 * Renders semantic `<ul>` or `<ol>` elements with configurable density,
 * dividers, marker styles, and an optional header.
 *
 * @example
 * ```
 * <List>
 *   <ListItem label="Notifications" description="Manage your alerts" />
 *   <ListItem label="Privacy" description="Control your data" />
 * </List>
 * <List listStyle="decimal" density="compact">
 *   <ListItem label="First step" />
 *   <ListItem label="Second step" />
 * </List>
 * ```
 */
export function List({
  children,
  density = 'balanced',
  hasDividers = false,
  header,
  listStyle = 'none',
  start,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ref
}) {
  const headerId = useId();
  const isOrdered = listStyle === 'decimal';
  const Tag = isOrdered ? 'ol' : 'ul';
  const contextValue = useMemo(() => ({
    density,
    hasDividers,
    listStyle
  }), [density, hasDividers, listStyle]);
  const listElement = /*#__PURE__*/_jsx(Tag, {
    ref: ref,
    "data-testid": testId,
    "aria-labelledby": header != null ? headerId : undefined,
    ...(isOrdered && start != null && start !== 1 ? {
      start
    } : {}),
    ...(listStyle === 'none' && !isOrdered ? {
      role: 'list'
    } : {}),
    ...mergeProps(themeProps('list', {
      density,
      listStyle
    }), stylex.props(styles.list, hasDividers && styles.withDividers, listStyle !== 'none' && (start != null && start !== 1 ? dynamicStyles.counterStart(start - 1) : styles.withCounter), xstyle), className, style),
    children: children
  });
  if (header == null) {
    return /*#__PURE__*/_jsx(ListContext, {
      value: contextValue,
      children: listElement
    });
  }
  return /*#__PURE__*/_jsx(ListContext, {
    value: contextValue,
    children: /*#__PURE__*/_jsxs("div", {
      ...{
        className: "x78zum5 xdt5ytf"
      },
      children: [/*#__PURE__*/_jsx("div", {
        id: headerId,
        ...{
          className: "x1p37lm5"
        },
        children: header
      }), listElement]
    })
  });
}
List.displayName = 'List';